import { useMemo, useState } from 'react';
import { Plug, Search } from 'lucide-react';
import { Input } from '@reflex/ui/components/ui/input';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { HubReadGuard } from '@reflex/ui/components/layout/NoReadAccessState';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { OrgPluginPicker } from '@/components/plugins/OrgPluginPicker';
import { ReleaseChannelOptIn } from '@/components/plugins/ReleaseChannelOptIn';
import { useAuth } from '@/hooks/auth/useAuth';
import { useOrganization } from '@/hooks/auth/useOrganization';
import { useOrgPlugins } from '@/hooks/plugins/useOrgPlugins';
import type { InstallationView } from '@/lib/types';

/**
 * Connect → Integrations: one list of plugins and integrations, with a
 * single search. Rows open plugin detail (`/configure/connect/plugins/:name`).
 * Integration config is embedded on that detail page when the user can
 * read integrations.
 */
export function IntegrationsPage() {
  return (
    <HubReadGuard permission="integrations:read">
      <IntegrationsPageBody />
    </HubReadGuard>
  );
}

function IntegrationsPageBody() {
  const { activeOrganizationId } = useAuth();
  const { activeOrganization, can } = useOrganization();
  const orgId = activeOrganizationId;
  const query = useOrgPlugins(orgId);
  const [search, setSearch] = useState('');
  const canManageReleaseChannels = can('org:manage');
  const enabledReleaseChannels = activeOrganization?.enabledReleaseChannels ?? [];

  const totalCount =
    (query.data?.installed.length ?? 0) +
    (query.data?.available.length ?? 0) +
    (query.data?.system.length ?? 0);

  const filtered = useMemo(() => {
    if (!query.data) return undefined;
    const q = search.trim().toLowerCase();
    if (!q) return query.data;
    const match = (row: InstallationView) =>
      row.pluginName.toLowerCase().includes(q) ||
      (row.description?.toLowerCase().includes(q) ?? false);
    return {
      installed: query.data.installed.filter(match),
      available: query.data.available.filter(match),
      system: query.data.system.filter(match),
    };
  }, [query.data, search]);

  const showSearch = totalCount > 0;

  return (
    <PageLayout maxWidth="4xl">
      <PageHeader
        iconVariant="box"
        icon={<Plug className="h-5 w-5 text-muted-foreground" />}
        title="Integrations"
        description="Install plugins and connect external services for your agents."
        docs="integrations"
      />

      {orgId && (
        <ReleaseChannelOptIn
          orgId={orgId}
          enabledChannels={enabledReleaseChannels}
          canManage={canManageReleaseChannels}
        />
      )}

      {showSearch && (
        <div className="relative">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/70"
          />
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search plugins and integrations…"
            aria-label="Search plugins and integrations"
            data-testid="integrations-search"
            className="pl-9"
          />
        </div>
      )}

      <AsyncContent
        isLoading={query.isLoading}
        isError={query.isError}
        loadingMessage="Loading integrations…"
        errorMessage="Failed to load integrations."
      >
        <OrgPluginPicker data={filtered} orgId={orgId} searchActive={search.trim().length > 0} />
      </AsyncContent>
    </PageLayout>
  );
}
